import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AdjustInputs } from './copilot-domain';
import { isAdjustComplete } from './copilot-validation';
import { toCalendarDateString } from './copilot-mapper';
import { RiskInputsState } from './hg-copilot-risk-inputs.component';
import { TabState } from './copilot-ui-types';
import { TradingInstrument } from '../../trading-instruments/trading-instrument.class';
import { OptionExpirationDescriptor } from '../../shell-communication/shell-dto-protocol';
import { DetectMethodChanges } from '../../utils';
import { DateTime } from 'luxon';

interface DateItem {
    value: string;   // "yyyy-MM-dd" key
    label: string;
    date: Date;
}

// Adjust questionnaire (FR-14..17): ONE expiration + PriceRange + MinProfit + MaxRisk.
//
// CR-20260724-001: this is the ONE tab routed to the composition engine, so it is the one tab that
// opts into the engine-only inputs (Fill Gap form + Harvest Peaks) — see the template's
// allowFillGap / allowHarvest. Select keeps the LLM path and neither control.
//
// CR-20260814-001 added the expiration selector. The engine shapes a P&L LINE and a P&L line is
// defined per expiration, so the run has to be told which one it addresses. SINGLE-select, not the
// Close tab's multi-select: two expirations are two lines, and the engine shapes one.
@Component({
    selector: 'ets-hg-copilot-adjust-tab',
    templateUrl: 'hg-copilot-adjust-tab.component.html',
    styleUrls: ['./hg-copilot-adjust-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgCopilotAdjustTabComponent {
    @Output() stateChange = new EventEmitter<TabState<AdjustInputs>>();

    // The underlying + the NEAREST-AVAILABLE expiration (both resolved by the parent and pushed down). Strike
    // selection always uses the nearest available expiration (owner ruling 2026-06-21, mirroring the Hedge
    // Matrix's getNearestExpiration). Forwarded as-is to the shared risk-inputs block.
    //
    // ⚠ DISTINCT from the expiration selector below. This one picks which chain the STRIKE dropdowns are
    // filled from; the selector picks which P&L LINE the run addresses. Different names, different things.
    @Input() targetInstrument: TradingInstrument | null = null;
    @Input() strikeExpiration: OptionExpirationDescriptor | null = null;

    // CR-20260831-004: the fields that no longer match what the CURRENT run sent, diffed by the
    // orchestrator (hg-copilot-param-diff grammar). This tab consumes 'expiration' itself and
    // forwards the whole map to the risk-inputs block. Null/empty = clean (or no run yet).
    @Input() changedKeys: { [key: string]: boolean } | null = null;

    // CR-20260901-001: pass-through to the risk-inputs block — the portfolio's default quantity
    // for the Normalized/Raw mode-flip value conversion.
    @Input() defaultQty: number | null | undefined = null;

    isChanged(key: string): boolean {
        return !!(this.changedKeys && this.changedKeys[key]);
    }

    dateItems: DateItem[] = [];
    selectedDateValue: string | null = null;

    private _dateByValue: { [value: string]: Date } = {};
    private _risk: RiskInputsState | null = null;
    private _emitScheduled = false;
    // One-shot latch for the single-expiration auto-selection. See the setter for why it is required.
    private _autoSelected = false;

    // The expirations this run may address, sourced from readAdjustDates() (the HELD-cell projection,
    // NOT readBookDates) so every chip is backed by a position the run will actually send.
    //
    // ⚠ THIS SETTER FIRES ON EVERY CHANGE-DETECTION PASS. The parent binds a getter that returns a
    // FRESH array each call, so Angular sees a new reference every cycle. Everything below is written
    // to be idempotent under that: the auto-selection is latched so it happens once and never fights
    // the trader's own choice, and an emit is scheduled only when the selection actually moved.
    @Input() set bookDates(dates: Date[]) {
        const items: DateItem[] = [];
        this._dateByValue = {};
        (dates || []).forEach(d => {
            const value = toCalendarDateString(d);
            this._dateByValue[value] = d;
            // Weekday + friendly expiration ("Fri 14-Aug-26"), matching the Close tab. The ISO string
            // stays the underlying value/key. d is local-midnight, so no UTC shift.
            items.push({ value, label: DateTime.fromJSDate(d).toFormat('EEE dd-MMM-yy'), date: d });
        });
        items.sort((a, b) => a.date.getTime() - b.date.getTime());   // chronological (nearest first)
        this.dateItems = items;

        const before = this.selectedDateValue;

        // A selection that is no longer in the book cannot be run — the trader edited the matrix and
        // that expiration went away. Clear it rather than let a stale value reach the position filter.
        if (this.selectedDateValue !== null && !this._dateByValue[this.selectedDateValue]) {
            this.selectedDateValue = null;
            this._autoSelected = false;
        }

        // Single expiration in the book: pick it FOR the trader, so the common case runs with no extra
        // click and behaves exactly as it did before this control existed. Latched, because this setter
        // re-runs constantly — without the latch every cycle would re-select and re-emit.
        if (!this._autoSelected && this.selectedDateValue === null && items.length === 1) {
            this.selectedDateValue = items[0].value;
            this._autoSelected = true;
        }

        if (this.selectedDateValue !== before) {
            this.scheduleEmit();
        }
    }

    get expirationLabel(): string {
        return this.dateItems.length === 1 ? 'Available Expiration' : 'Available Expirations';
    }

    get hasSelectedDate(): boolean {
        return this.selectedDateValue !== null;
    }

    isDateSelected(value: string): boolean {
        return this.selectedDateValue === value;
    }

    // SINGLE-select: choosing ASSIGNS, it never toggles into a list. Clicking the already-selected chip
    // is a no-op rather than a deselect — an Adjust run always needs an expiration, so there is no
    // useful "none" state for the trader to reach by clicking twice.
    @DetectMethodChanges()
    selectDate(value: string): void {
        if (this.selectedDateValue === value) {
            return;
        }
        this.selectedDateValue = value;
        this._autoSelected = true;   // the trader has chosen; never auto-override afterwards
        this.emitState();            // user-initiated, so outside the parent's CD pass: safe to emit now
    }

    onRiskChange(s: RiskInputsState): void {
        this._risk = s;
        this.emitState();
    }

    // Emits originating from the @Input setter run INSIDE the parent's change-detection pass, and
    // emitting an @Output from there throws ExpressionChangedAfterItHasBeenChecked (the parent's
    // canRun reads the tab state this emit rewrites). Deferring by a turn moves the emit past that
    // pass. Coalesced, because the setter can fire many times before the timeout lands.
    private scheduleEmit(): void {
        if (this._emitScheduled) {
            return;
        }
        this._emitScheduled = true;
        setTimeout(() => {
            this._emitScheduled = false;
            this.emitState();
        }, 0);
    }

    private emitState(): void {
        if (!this._risk) {
            return;   // the risk block has not reported yet; there is nothing complete to emit
        }
        // No engine directive and no strictness flag (retired 2026-08-08): the backend resolves
        // its one composer set at its own boundary, which is the one place a default belongs.
        const inputs: AdjustInputs = {
            expiration: this.selectedDateValue ? this._dateByValue[this.selectedDateValue] : null,
            priceRange: this._risk.priceRange,
            expectedRangeModel: this._risk.expectedRangeModel,
            minProfit: this._risk.minProfit,
            minProfitNormalized: this._risk.minProfitNormalized,
            maxRisk: this._risk.maxRisk,
            harvest: this._risk.harvest,
            riskSide: this._risk.riskSide,
            halfRisk: this._risk.halfRisk
        };
        this.stateChange.emit({ inputs, valid: isAdjustComplete(inputs) });
    }
}
